// Generic, fetch-based ERP entity client. A module singleton so any app can
// `import { ErpEntityClient }` and query ERP resources (products, customers, …)
// through the shared gateway — no per-app backend proxy required.
//
// Data path: GET {origin}/v1/{resource} with `BLP-Module: erp` + the user's
// bearer. Tenancy + permissions resolve server-side from the token (this is the
// same call ERP's own autocomplete makes).
//
// The DSL is ported from frontend/erp's useFetchAutocompleteRelation. fetch (not
// axios) keeps connect dependency-free; mind the differences (no 4xx reject,
// no `.data` wrapper, no auth-refresh interceptor).

import { localeHeader } from '../http/locale';
import type {
  ErpAutocompleteResult,
  ErpBaseFilterValue,
  ErpEntityRow,
  ErpListParams,
  ErpListResult,
} from './types';

export type {
  ErpAutocompleteResult,
  ErpBaseFilterValue,
  ErpEntityRow,
  ErpListParams,
  ErpListResult,
} from './types';

/**
 * Config the host app pushes in once at startup (mirrors how the Connect shell
 * feeds `NxSocketClient` its bearer). Without it, the client falls back to the
 * localStorage keys every Formica app already writes.
 */
export interface ErpEntityClientConfig {
  /** Bearer token WITHOUT the "Bearer " prefix. */
  getBearer: () => string;
  /** API origin WITHOUT a trailing `/v1` (e.g. `http://localhost:7680`). */
  getApiOrigin: () => string;
  /** Gateway module to route to. Defaults to `'erp'`. */
  module?: string;
}

export class ErpClientError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: string,
  ) {
    super(`ERP request failed: ${status}`);
    this.name = 'ErpClientError';
  }
}

let cfg: ErpEntityClientConfig | null = null;

function configure(next: ErpEntityClientConfig): void {
  cfg = next;
}

// --- fallbacks (keys verified in frontend/connect/src/auth.ts + frontend/erp/src/api/api.ts) ---
const TOKEN_KEY = 'blp_token';
const API_BASE_KEY = 'formica.api.baseUrl';

function stripV1(url: string): string {
  return url.replace(/\/v1\/?$/, '').replace(/\/+$/, '');
}

function fallbackBearer(): string {
  try {
    return localStorage.getItem(TOKEN_KEY) ?? '';
  } catch {
    return '';
  }
}

function fallbackApiOrigin(): string {
  try {
    const stored = localStorage.getItem(API_BASE_KEY);
    if (stored) return stripV1(stored);
  } catch {
    /* localStorage unavailable */
  }
  const host = (typeof window !== 'undefined' && window.location?.hostname) || 'localhost';
  return `http://${host}:7680`;
}

function bearer(): string {
  return cfg?.getBearer() || fallbackBearer();
}

function apiOrigin(): string {
  const origin = cfg?.getApiOrigin();
  return origin ? stripV1(origin) : fallbackApiOrigin();
}

function moduleHeader(): string {
  return cfg?.module ?? 'erp';
}

function buildUrl(resource: string, params: Record<string, unknown>): string {
  const usp = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    usp.append(key, String(value));
  }
  const qs = usp.toString();
  return `${apiOrigin()}/v1/${resource}${qs ? `?${qs}` : ''}`;
}

function authHeaders(): Headers {
  const headers = new Headers({ Accept: 'application/json', 'BLP-Module': moduleHeader(), ...localeHeader() });
  const token = bearer();
  if (token) headers.set('Authorization', `Bearer ${token}`);
  return headers;
}

type Envelope = Record<string, unknown> & {
  metadata?: { count?: number; pages?: number; currentPage?: number };
};

async function getJson(resource: string, params: Record<string, unknown>): Promise<Envelope> {
  const url = buildUrl(resource, params);
  // fetch does NOT reject on 4xx/5xx — check res.ok ourselves.
  const res = await fetch(url, { method: 'GET', headers: authHeaders() });
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new ErpClientError(res.status, `${url} → ${body.slice(0, 200)}`);
  }
  return (await res.json()) as Envelope;
}

function readRows(json: Envelope, resource: string): ErpEntityRow[] {
  const direct = json[resource];
  if (Array.isArray(direct)) return direct as ErpEntityRow[];
  // The envelope key isn't always the resource name — fall back to the first
  // non-metadata array (mirrors frontend/erp's useFetchAutocompleteRelation).
  for (const [key, value] of Object.entries(json)) {
    if (key === 'metadata') continue;
    if (Array.isArray(value)) return value as ErpEntityRow[];
  }
  return [];
}

function unique(items: string[]): string[] {
  return [...new Set(items)];
}

// Root-level pre-filters, AND-combined by the backend. A bare scalar keeps the
// original `eq` shape; the object form emits its own operator, which is how a
// caller expresses a set (`{ op: 'in', value: 'draft,open' }` → one
// `filter[status][in]=draft,open`, the ERP listing DSL's comma-separated list).
function applyBaseFilter(
  params: Record<string, unknown>,
  baseFilter: Map<string, ErpBaseFilterValue> | undefined,
): void {
  if (!baseFilter) return;
  for (const [col, entry] of baseFilter) {
    const op = typeof entry === 'object' ? entry.op : 'eq';
    const value = typeof entry === 'object' ? entry.value : entry;
    params[`filter[${col}][${op}]`] = value;
  }
}

// Build the OR search group: id[eq] for an all-digit query (unless ignored),
// plus [col][ilike] for each column. `mode` wraps the value `q%` (prefix, for
// the type-ahead) or `%q%` (contains, for the browse list).
function applySearch(
  params: Record<string, unknown>,
  columns: string[],
  query: string,
  ignoreId: boolean | undefined,
  mode: 'prefix' | 'contains',
): void {
  if (!query || !columns.length) return;
  const pattern = mode === 'contains' ? `%${query}%` : `${query}%`;
  let i = 0;
  // `id` is bigint: only match it for an all-digit query, else pgx 500s.
  if (!ignoreId && /^\d+$/.test(query)) params[`filter[or][${i++}][id][eq]`] = query;
  for (const col of columns) params[`filter[or][${i++}][${col}][ilike]`] = pattern;
}

interface SearchAutocompleteArgs {
  resource: string;
  /** Column projected for the label (returned in each row). */
  displayColumn: string;
  /** Columns matched by the query (OR-combined). */
  searchColumns: string[];
  query: string;
  baseFilter?: Map<string, ErpBaseFilterValue>;
  /** Never match the bigint id column. */
  searchIgnoreId?: boolean;
  limit?: number;
}

/** Type-ahead search: prefix `ilike` over searchColumns (+ id for digits), bigint-guarded. */
async function searchAutocomplete(args: SearchAutocompleteArgs): Promise<ErpAutocompleteResult> {
  const { resource, displayColumn, searchColumns, query, baseFilter, searchIgnoreId, limit = 10 } = args;
  const params: Record<string, unknown> = {
    only: unique(['id', displayColumn, ...searchColumns]).join(','),
    limit,
  };
  applyBaseFilter(params, baseFilter);
  applySearch(params, searchColumns, query, searchIgnoreId, 'prefix');
  const json = await getJson(resource, params);
  const meta = json.metadata ?? {};
  const rows = readRows(json, resource);
  return { rows, pages: meta.pages ?? 1, count: meta.count ?? rows.length };
}

/** Paginated, multi-column list — drives both the light-list modal and the grid. */
async function list<Row extends ErpEntityRow = ErpEntityRow>(
  p: ErpListParams,
): Promise<ErpListResult<Row>> {
  const params: Record<string, unknown> = { page: p.page ?? 1, perPage: p.perPage ?? 25 };
  if (p.only?.length) params.only = p.only.join(',');
  if (p.sortColumn) {
    params.sortColumn = p.sortColumn;
    params.sortOrder = p.sortOrder ?? 'asc';
  }
  applyBaseFilter(params, p.baseFilter);
  if (p.filters) {
    for (const [col, f] of Object.entries(p.filters)) params[`filter[${col}][${f.op}]`] = f.value;
  }
  if (p.search) applySearch(params, p.search.columns, p.search.query, p.search.ignoreId, 'contains');
  const json = await getJson(p.resource, params);
  const meta = json.metadata ?? {};
  const rows = readRows(json, p.resource) as Row[];
  return {
    rows,
    count: meta.count ?? rows.length,
    pages: meta.pages ?? 1,
    currentPage: meta.currentPage ?? p.page ?? 1,
  };
}

/** Resolve a stored id back to its display label (for consumers that store only the FK). */
async function fetchById(
  resource: string,
  id: number,
  displayColumn: string,
): Promise<string | null> {
  const json = await getJson(resource, {
    only: `id,${displayColumn}`,
    'filter[id][eq]': id,
    limit: 1,
  });
  const row = readRows(json, resource)[0];
  if (!row) return null;
  const label = row[displayColumn];
  return label == null ? null : String(label);
}

export const ErpEntityClient = {
  configure,
  searchAutocomplete,
  list,
  fetchById,
} as const;
